<template>
  <div class="content cobertura-gsm">
    <div class="container-fluid">
      <!-- Parámetros -------------------------------------------------------->
      <div class="card">
        <div class="card-header">
          <h4 class="card-title">Informe Cobertura GSM</h4>
          <p class="card-category">
            Pérdidas de señal y cobertura del recorrido · hasta {{ maxVehiculos }} unidades ·
            períodos de hasta {{ maxDias }} días
          </p>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-3">
              <label class="cg-label">Desde</label>
              <input v-model="desde" type="datetime-local" class="form-control" />
            </div>
            <div class="col-md-3">
              <label class="cg-label">Hasta</label>
              <input v-model="hasta" type="datetime-local" class="form-control" />
            </div>
            <div class="col-md-6">
              <label class="cg-label">Patentes</label>
              <div class="cg-patentes">
                <div v-for="(p, i) in patentes" :key="i" class="cg-patente">
                  <i class="cg-sw" :style="{ background: color(i) }"></i>
                  <input
                    v-model="patentes[i]"
                    type="text"
                    class="form-control cg-input-patente"
                    placeholder="AB123CD"
                    maxlength="12"
                    @keyup.enter="generar"
                  />
                  <button
                    v-if="patentes.length > 1"
                    type="button"
                    class="btn btn-default btn-xs"
                    title="Quitar"
                    @click="quitar(i)"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <button
                v-if="patentes.length < maxVehiculos"
                type="button"
                class="btn btn-default btn-xs cg-agregar"
                @click="patentes.push('')"
              >
                + Agregar unidad
              </button>
            </div>
          </div>

          <div class="cg-acciones">
            <button
              type="button"
              class="btn btn-fill btn-info"
              :disabled="cargando"
              @click="generar"
            >
              {{ cargando ? "Generando…" : "Generar informe" }}
            </button>
            <button
              v-if="unidades.length"
              data-ayuda="Baja un archivo HTML con las tarjetas, la comparativa, los gráficos y el detalle. Se abre en cualquier navegador, se manda por mail y se imprime a PDF con Ctrl+P. El mapa no entra: necesita estar conectado."
              type="button"
              class="btn btn-default"
              @click="exportar"
            >
              Exportar para compartir
            </button>
            <span v-if="ventanaTexto" class="cg-ventana">{{ ventanaTexto }}</span>
          </div>

          <div v-if="error" class="alert alert-danger cg-error">{{ error }}</div>
        </div>
      </div>

      <!-- Estado inicial ---------------------------------------------------->
      <div v-if="!unidades.length && !error" class="card">
        <div class="card-body cg-vacio">
          <b>Todavía no generaste ningún informe.</b>
          <p>
            Elegí el período y cargá de 1 a {{ maxVehiculos }} patentes para ver dónde y cuándo
            perdieron señal esas unidades.
          </p>
        </div>
      </div>

      <!-- Resultados -------------------------------------------------------->
      <template v-if="unidades.length">
        <div class="row">
          <div v-for="u in unidades" :key="u.uid" class="col-md-4 col-sm-6">
            <div class="card cg-unidad" :style="{ borderTopColor: u.hex }">
              <div class="card-body">
                <div class="cg-cap">
                  <template v-if="u.equipo">Equipo {{ u.equipo }}</template>
                  <template v-else>Equipo sin identificar</template>
                  <template v-if="u.equipoRed"> · {{ u.equipoRed }}</template>
                  <template v-if="u.chipEmpresa"> · {{ u.chipEmpresa }}</template>
                </div>
                <div class="cg-alias">
                  <i class="cg-sw" :style="{ background: u.hex }"></i>
                  {{ u.alias }}<span class="cg-pat">{{ u.patente }}</span>
                </div>

                <div class="cg-big">
                  {{ pctTexto(conexionDe(u).enVivoPct) }}<small>en vivo</small>
                </div>

                <dl class="cg-dl">
                  <dt>Cortes de conexión*</dt>
                  <dd>{{ nf(conexionDe(u).cortes) }}</dd>
                  <dt>El más largo</dt>
                  <dd>{{ conexionDe(u).masLargoMin ? fmtDurHm(conexionDe(u).masLargoMin) : "—" }}</dd>
                  <dt>Posiciones</dt>
                  <dd>{{ nf(conexionDe(u).posiciones) }}</dd>
                  <dt>Con demora**</dt>
                  <dd>{{ pctTexto(conexionDe(u).conDemoraPct) }}</dd>
                </dl>

                <!-- Integridad: un corte de conexión no implica perder el recorrido. -->
                <div class="cg-integridad" :class="conexionDe(u).integro ? 'ok' : 'falta'">
                  <template v-if="conexionDe(u).integro">
                    Información completa: el recorrido llegó entero al reconectarse.
                  </template>
                  <template v-else>
                    {{ nf(conexionDe(u).sinRecuperar) }}
                    {{ conexionDe(u).sinRecuperar === 1 ? "corte" : "cortes" }} sin recuperar: falta
                    el recorrido de
                    {{ conexionDe(u).sinRecuperar === 1 ? "ese tramo" : "esos tramos" }}.
                  </template>
                </div>

                <div class="cg-pie">
                  <span>{{ u.cliente || "—" }}</span>
                  <span class="cg-mono">chip {{ u.chipNumero || "—" }} · {{ u.imei }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p class="cg-aclaracion">
          <b>*</b> Corte de conexión: más de 16 minutos sin transmitir. El equipo reporta cada 8
          minutos como máximo, en marcha o detenido, así que un silencio más largo es un corte.
          <b>**</b> Con demora: posiciones que el equipo no pudo enviar en el momento, las guardó en
          memoria y llegaron al reconectarse.
        </p>
        <p class="cg-aclaracion">
          Las tarjetas miden la <b>conexión</b>: cuánto tiempo el equipo estuvo transmitiendo.
          De acá para abajo se mide la <b>señal</b>, con los pares de alertas 107 (pérdida) y 108
          (reenganche), que es otra cosa y da otros números.
        </p>

        <div class="card">
          <div class="card-body cg-filtro">
            <span class="cg-label">Solo cortes de señal mayores a</span>
            <input v-model.number="minFiltro" type="range" min="0" max="240" step="5" />
            <span class="cg-mono cg-filtro-val">{{ minFiltro }} min</span>
          </div>
        </div>

        <graficos-cobertura :unidades="unidades" :min-filtro="minFiltro" />
        <tabla-cortes :unidades="unidades" :min-filtro="minFiltro" @ver-tramo="verTramo" />
        <mapa-cobertura v-if="hayMapa" ref="mapa" :unidades="unidadesConMapa" />
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import get from "lodash-es/get";
import GraficosCobertura from "./CoberturaGsm/GraficosCobertura.vue";
import MapaCobertura from "./CoberturaGsm/MapaCobertura.vue";
import TablaCortes from "./CoberturaGsm/TablaCortes.vue";
import { descargarInforme } from "./CoberturaGsm/exportarInforme";
import {
  PALETTE,
  aParametroSql,
  conexion,
  fmtDT,
  fmtDurHm,
  nf,
  parseUnidad,
  pct,
} from "./CoberturaGsm/cobertura";

const SP = "CLI.GetInformeCoberturaGSM";

export default {
  name: "InformeCoberturaGsm",
  components: { GraficosCobertura, MapaCobertura, TablaCortes },
  data() {
    return {
      // Los límites los manda el SP; estos son solo el valor inicial hasta
      // que responde, para no dejar el formulario sin nada.
      maxVehiculos: 4,
      maxDias: 14,
      desde: "",
      hasta: "",
      patentes: [""],
      minFiltro: 20,
      unidades: [],
      ventanaTexto: "",
      error: "",
      cargando: false,
    };
  },
  computed: {
    unidadesConMapa() {
      return this.unidades.filter((u) => u.puntos.length);
    },
    hayMapa() {
      return this.unidadesConMapa.length > 0;
    },
  },
  created() {
    this.periodoPorDefecto();
    this.traerLimites();
  },
  methods: {
    color(i) {
      return PALETTE[i % PALETTE.length];
    },
    quitar(i) {
      this.patentes.splice(i, 1);
    },

    /** Arranca con los últimos 7 días completos. */
    periodoPorDefecto() {
      const hasta = new Date();
      hasta.setSeconds(0, 0);
      const desde = new Date(hasta.getTime() - 7 * 86400000);
      desde.setHours(0, 0, 0, 0);
      this.desde = this.aInput(desde);
      this.hasta = this.aInput(hasta);
    },
    aInput(d) {
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(
        d.getMinutes(),
      )}`;
    },

    /**
     * El SP es el dueño de los límites: si allá cambian 4 por 6, o 14 días por
     * 30, la pantalla se adapta sola y no hay que tocar nada acá.
     */
    async traerLimites() {
      const data = await this.ejecutar([{ name: "SoloLimites", value: 1 }], true);
      if (data && data.limites) {
        this.maxVehiculos = data.limites.max_vehiculos;
        this.maxDias = data.limites.max_dias;
      }
    },

    async generar() {
      this.cargando = true;
      this.error = "";
      const data = await this.ejecutar([
        { name: "Patentes", value: this.patentes.map((p) => p.trim()).filter(Boolean).join(",") },
        { name: "Desde", value: aParametroSql(this.desde) },
        { name: "Hasta", value: aParametroSql(this.hasta) },
      ]);
      this.cargando = false;
      if (!data) return;

      if (data.limites) {
        this.maxVehiculos = data.limites.max_vehiculos;
        this.maxDias = data.limites.max_dias;
      }

      // El error lo redacta el SP: acá se muestra tal cual, sin reescribirlo.
      if (data.ok === false) {
        this.unidades = [];
        this.error = data.error || "No se pudo generar el informe.";
        return;
      }

      this.unidades = (data.unidades || []).map((u, i) => parseUnidad(u, i));
      this.ventanaTexto = this.unidades.length
        ? `${fmtDT(this.unidades[0].desde)}  →  ${fmtDT(this.unidades[0].hasta)}`
        : "";
      if (!this.unidades.length) {
        this.error = "El informe volvió sin unidades. Revisá las patentes y el período.";
      }
    },

    /**
     * FOR JSON parte el resultado en varias filas cuando pasa los 2033
     * caracteres, así que hay que pegarlas de nuevo antes de parsear.
     */
    async ejecutar(parameters, silencioso) {
      try {
        const resp = await axios.post("/execute", { method: SP, parameters });
        if (!get(resp, "data.ok", false)) {
          if (!silencioso) this.error = get(resp, "data.message", "No se pudo ejecutar el informe.");
          return null;
        }
        const filas = get(resp, "data.result[0]", []);
        const json = filas.map((f) => Object.values(f)[0]).join("");
        if (!json) {
          if (!silencioso) this.error = "El informe volvió vacío.";
          return null;
        }
        return JSON.parse(json);
      } catch (e) {
        console.error(e);
        if (!silencioso) this.error = "No se pudo conectar con el servidor para generar el informe.";
        return null;
      }
    },

    conexionDe(u) {
      return conexion(u);
    },
    pctTexto(v) {
      return pct(v);
    },
    nf,
    fmtDurHm,

    verTramo(uid, t0, t1) {
      if (this.$refs.mapa) this.$refs.mapa.verTramo(uid, t0, t1);
    },

    /** Los gráficos se reusan tal cual: son los SVG que ya están en pantalla. */
    exportar() {
      const graficos = [...this.$el.querySelectorAll(".cg-chart")].map((s) => s.outerHTML);
      const ok = descargarInforme({
        unidades: this.unidades,
        graficos,
        minFiltro: this.minFiltro,
      });
      if (!ok) this.error = "No hay nada para exportar todavía.";
    },
  },
};
</script>

<style>
/* Tokens de marca: el módulo administrativo no los tiene cargados.
   El chrome (fondo, texto, líneas) usa los grises del propio módulo, porque el
   manual de marca es dark-first y no documenta una variante clara. */
.cobertura-gsm {
  --mq-navy-900: #0a1224;
  --mq-bone-50: #f5f4f0;
  --mq-orange: #ffa00f;
  --mq-red: #e93241;
  --mq-info: #6fa3e0;
  --mq-success: #6fc06f;

  --cg-text: #333333;
  --cg-muted: #9a9a9a;
  --cg-line: #e3e3e3;
  --cg-surface: #ffffff;
  --cg-surface-alt: #f7f7f7;
  --cg-font-mono: ui-monospace, "Cascadia Code", "SF Mono", Consolas, monospace;
}

/* Ayuda al pasar el mouse.
   Va con CSS propio y no con v-tooltip: esa librería no trae su hoja de estilos
   y Bootstrap define `.tooltip{opacity:0}`, así que el tooltip se abría pero no
   se veía. Con `data-ayuda="..."` en cualquier elemento alcanza.
   El retardo es a propósito: aparece recién si el mouse se queda quieto. */
.cobertura-gsm [data-ayuda] {
  position: relative;
}
.cobertura-gsm [data-ayuda]::after {
  content: attr(data-ayuda);
  position: absolute;
  bottom: calc(100% + 9px);
  right: 0;
  z-index: 1200;
  width: 290px;
  padding: 9px 11px;
  border-radius: 6px;
  background: var(--mq-navy-900);
  color: var(--mq-bone-50);
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 400;
  line-height: 1.45;
  text-align: left;
  text-transform: none;
  letter-spacing: 0;
  white-space: normal;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease 0.45s, visibility 0s linear 0.6s;
}
.cobertura-gsm [data-ayuda]:hover::after {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.15s ease 0.45s, visibility 0s linear 0.45s;
}
/* :focus-visible y no :focus. Con :focus, al hacer clic el botón queda enfocado
   y la ayuda se quedaba pegada hasta clickear en otro lado; se notaba sobre todo
   en "Centrar", que no cambia de estado al tocarlo. :focus-visible se activa
   navegando con el teclado, que es cuando la ayuda hace falta sin mouse.
   (En el bundle el minificador une esta regla con la de :hover, así que no sirve
   de nada separarlas para protegerse de navegadores que no lo soporten; los del
   browserslist del proyecto lo soportan.) */
.cobertura-gsm [data-ayuda]:focus-visible::after {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.15s ease 0.45s, visibility 0s linear 0.45s;
}
</style>

<style scoped>
.cg-label {
  font-family: var(--cg-font-mono);
  font-size: 9.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--cg-muted);
  display: block;
  margin-bottom: 4px;
}
.cg-patentes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cg-patente {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cg-input-patente {
  width: 130px;
  text-transform: uppercase;
  font-family: var(--cg-font-mono);
}
.cg-sw {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  flex: 0 0 auto;
}
.cg-agregar {
  margin-top: 8px;
}
.cg-acciones {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  flex-wrap: wrap;
}
.cg-ventana {
  font-family: var(--cg-font-mono);
  font-size: 11px;
  color: var(--cg-muted);
  font-variant-numeric: tabular-nums;
}
.cg-error {
  margin-top: 16px;
  margin-bottom: 0;
}
.cg-vacio {
  text-align: center;
  color: var(--cg-muted);
  padding: 40px 20px;
}
.cg-vacio b {
  color: var(--cg-text);
  font-weight: 500;
  font-size: 15px;
}
.cg-vacio p {
  margin: 8px 0 0;
}
/* Tarjeta por unidad, siguiendo la del informe que se le manda al cliente. */
.cg-unidad {
  border-top: 3px solid var(--cg-line);
}
.cg-cap {
  font-family: var(--cg-font-mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--cg-muted);
}
.cg-alias {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin-top: 2px;
  color: var(--cg-text);
}
.cg-alias .cg-sw {
  width: 9px;
  height: 9px;
  margin-right: 5px;
}
.cg-pat {
  font-family: var(--cg-font-mono);
  font-size: 11px;
  font-weight: 400;
  opacity: 0.65;
  margin-left: 6px;
}
.cg-big {
  font-size: 30px;
  font-weight: 500;
  letter-spacing: -0.02em;
  margin: 8px 0 0;
  font-variant-numeric: tabular-nums;
  color: var(--cg-text);
}
.cg-big small {
  font-size: 12px;
  font-weight: 400;
  color: var(--cg-muted);
  letter-spacing: 0;
  margin-left: 4px;
}
.cg-dl {
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 10px;
  font-size: 12px;
}
.cg-dl dt {
  color: var(--cg-muted);
  font-weight: 400;
}
.cg-dl dd {
  margin: 0;
  text-align: right;
  white-space: nowrap;
  font-family: var(--cg-font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--cg-text);
}
.cg-integridad {
  margin-top: 12px;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 11.5px;
  line-height: 1.4;
}
.cg-integridad.ok {
  background: #eef6ee;
  color: #2f6b2f;
}
.cg-integridad.falta {
  background: #fdecee;
  color: #a11f2b;
}
.cg-pie {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--cg-line);
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: var(--cg-muted);
}
.cg-mono {
  font-family: var(--cg-font-mono);
  font-variant-numeric: tabular-nums;
}
.cg-aclaracion {
  font-size: 12.5px;
  color: var(--cg-muted);
  margin: 4px 0 14px;
  line-height: 1.55;
}
.cg-aclaracion b {
  color: var(--cg-text);
  font-weight: 500;
}
.cg-filtro {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.cg-filtro input[type="range"] {
  width: 240px;
  accent-color: var(--mq-orange);
}
.cg-filtro-val {
  font-size: 14px;
  color: var(--cg-text);
}
</style>
